import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import * as signalR from '@microsoft/signalr'
import config from '../config'

interface WebSocketContextType {
  connection: signalR.HubConnection | null
  isConnected: boolean
  connectionState: signalR.HubConnectionState
  activeCalls: any[]
  notifications: any[]
  sendMessage: (method: string, ...args: any[]) => Promise<void>
  subscribeToChannel: (channel: string, callback: (...args: any[]) => void) => void
  unsubscribeFromChannel: (channel: string) => void
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined)

interface WebSocketProviderProps {
  children: ReactNode
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionState, setConnectionState] = useState<signalR.HubConnectionState>(signalR.HubConnectionState.Disconnected)
  const [activeCalls, setActiveCalls] = useState<any[]>([])
  const [notifications, setNotifications] = useState<any[]>([])

  useEffect(() => {
    // For demo: simulate real-time events every 15-30 seconds
    const simulateRealtimeEvents = () => {
      const events = [
        {
          type: 'call-started',
          title: 'International Call Started',
          message: 'New call from Tokyo - AI adapting to Japanese business culture',
          severity: 'info'
        },
        {
          type: 'cultural-adaptation',
          title: 'Cultural Intelligence Active',
          message: 'AI successfully bridged communication between German directness and Brazilian warmth',
          severity: 'success'
        },
        {
          type: 'revenue-milestone',
          title: 'Revenue Milestone!',
          message: '$15,000+ generated this month through AI interactions',
          severity: 'success'
        },
        {
          type: 'language-detection',
          title: 'New Language Detected',
          message: 'AI automatically switched to Mandarin for Beijing client',
          severity: 'info'
        },
        {
          type: 'ai-insight',
          title: 'AI Insight Generated',
          message: 'Identified potential partnership opportunity with Dubai client',
          severity: 'info'
        }
      ]
      
      const randomEvent = events[Math.floor(Math.random() * events.length)]
      
      setNotifications(prev => [{
        id: Date.now(),
        type: randomEvent.type,
        title: randomEvent.title,
        message: randomEvent.message,
        timestamp: new Date(),
        severity: randomEvent.severity
      }, ...prev.slice(0, 9)]) // Keep only last 10 notifications
    }

    // Simulate events every 15-30 seconds during business hours
    const now = new Date()
    const isBusinessHours = now.getHours() >= 9 && now.getHours() <= 17
    const interval = isBusinessHours ? 15000 + Math.random() * 15000 : 45000 + Math.random() * 30000
    
    const eventTimer = setInterval(simulateRealtimeEvents, interval)
    
    // Initial event after 3 seconds
    setTimeout(simulateRealtimeEvents, 3000)

    // Create SignalR connection with auth token for proper group assignment.
    // Read fresh from localStorage on every call so post-refresh reconnects pick
    // up the new token. Capturing into a const here would freeze the value at
    // hub-creation time and break auto-reconnect after a token refresh.
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${config.backendUrl}${config.hubs.dashboard}`, {
        accessTokenFactory: () => localStorage.getItem('authToken') || ''
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Retry: immediate, 2s, 5s, 10s, 30s
      .configureLogging(signalR.LogLevel.Warning) // Reduce noise
      .build()

    // Set up connection event handlers
    hubConnection.onclose(() => {
      setIsConnected(false)
      setConnectionState(hubConnection.state)
    })

    hubConnection.onreconnecting(() => {
      setIsConnected(false)
      setConnectionState(hubConnection.state)
    })

    hubConnection.onreconnected(() => {
      setIsConnected(true)
      setConnectionState(hubConnection.state)
    })

    // Set up real-time event handlers
    hubConnection.on('CallStarted', (callData) => {
      setActiveCalls(prev => [...prev, callData])
      // OOM fix: cap notifications to prevent unbounded growth
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'call-started',
        title: 'New Call Started',
        message: `Call from ${callData.callerNumber}`,
        timestamp: new Date(),
        data: callData
      }].slice(-50))
    })

    hubConnection.on('CallEnded', (callId) => {
      setActiveCalls(prev => prev.filter(call => call.id !== callId))
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'call-ended',
        title: 'Call Ended',
        message: 'Call has been completed',
        timestamp: new Date()
      }].slice(-50))
    })

    hubConnection.on('CallTransferred', (transferData) => {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'call-transferred',
        title: 'Call Transferred',
        message: `Call transferred to ${transferData.agentName}`,
        timestamp: new Date(),
        data: transferData
      }].slice(-50))
    })

    hubConnection.on('MetricsUpdate', (metrics) => {
      // Handle real-time metrics updates
      console.log('Metrics updated:', metrics)
    })

    hubConnection.on('SystemAlert', (alert) => {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'system-alert',
        title: alert.title,
        message: alert.message,
        timestamp: new Date(),
        severity: alert.severity
      }].slice(-50))
    })

    hubConnection.on('AIInsight', (insight) => {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'ai-insight',
        title: 'AI Insight',
        message: insight.message,
        timestamp: new Date(),
        data: insight
      }].slice(-50))
    })

    // Start connection
    const startConnection = async () => {
      try {
        await hubConnection.start()
        setIsConnected(true)
        setConnectionState(hubConnection.state)
        console.log('SignalR connected successfully')
        
        // Try to join groups, but handle gracefully if methods don't exist
        try {
          // Check if the method exists before calling
          if (hubConnection.connectionId) {
            // The backend may handle group joining automatically based on auth
            console.log('Connected with ID:', hubConnection.connectionId)
            
            // Try to join groups if the methods exist
            try {
              await hubConnection.invoke('JoinGroup', 'dashboard')
              await hubConnection.invoke('JoinGroup', 'admin')
            } catch (groupError: any) {
              if (groupError?.message?.includes('Method does not exist')) {
                console.log('Group joining handled automatically by server')
              } else {
                console.warn('Group joining error:', groupError)
              }
            }
          }
        } catch (groupError) {
          console.warn('Group joining not supported:', groupError)
          // Continue without groups - the backend may handle this differently
        }
      } catch (error) {
        console.error('SignalR connection failed:', error)
        setIsConnected(false)
        setConnectionState(hubConnection.state)
      }
    }

    setConnection(hubConnection)
    startConnection()

    // Cleanup on unmount — unregister handlers to prevent leaked closures
    return () => {
      clearInterval(eventTimer)
      hubConnection.off('CallStarted')
      hubConnection.off('CallEnded')
      hubConnection.off('CallTransferred')
      hubConnection.off('MetricsUpdate')
      hubConnection.off('SystemAlert')
      hubConnection.off('AIInsight')
      hubConnection.stop()
    }
  }, [])

  const sendMessage = async (method: string, ...args: any[]) => {
    if (connection && isConnected) {
      try {
        await connection.invoke(method, ...args)
      } catch (error) {
        console.error(`Failed to send message ${method}:`, error)
      }
    }
  }

  const subscribeToChannel = (channel: string, callback: (...args: any[]) => void) => {
    if (connection) {
      connection.on(channel, callback)
    }
  }

  const unsubscribeFromChannel = (channel: string) => {
    if (connection) {
      connection.off(channel)
    }
  }

  const value: WebSocketContextType = {
    connection,
    isConnected,
    connectionState,
    activeCalls,
    notifications,
    sendMessage,
    subscribeToChannel,
    unsubscribeFromChannel
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext)
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}